// Light/dark theme state for the redesigned console (#376).
//
// The boot script in index.html applies the stored theme (or light by default) to
// <html data-theme="…"> before first paint, so there is no flash. This module is
// the runtime side: a React hook around the same attribute + localStorage key.
import React from "react";

export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "neo.theme";

export function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

/** New Cockpit sessions start in the homepage's light presentation. */
export function defaultTheme(): Theme {
  return "light";
}

export function currentTheme(): Theme {
  const attr = document.documentElement.dataset.theme;
  return isTheme(attr) ? attr : defaultTheme();
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Private mode / blocked storage: the choice still applies for this tab.
  }
}

export function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setThemeState] = React.useState<Theme>(() => currentTheme());
  const setTheme = React.useCallback((next: Theme) => {
    applyTheme(next);
    setThemeState(next);
  }, []);
  return [theme, setTheme];
}
